import React, { FC, useEffect, useMemo, useState } from "react"
import { users } from "../../../../../apis"
import { mapToCamelCase } from "../../../../../utility"
import { AdvanceDealDetails, AdvanceDealPlatformRevenue } from "../../../../../types"
import AdvanceRecoupmentSchedule from "./AdvanceRecoupmentSchedule"
import PlatformRevenueChart from "./PlatformRevenueChart"

type AdvanceDealDetailsSectionsProps = {
  userId: number
  artistName: string
  // Matches the portfolio's period filter: the endpoint's default is the reporting-period view.
  reportingPeriod: boolean
}

// One call to /users/user_advance_portfolio_details/ feeds every section built on it.
const AdvanceDealDetailsSections: FC<AdvanceDealDetailsSectionsProps> = ({ userId, artistName, reportingPeriod }) => {
  const [details, setDetails] = useState<AdvanceDealDetails | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  // Limits the Platform Revenue chart to the tracks hit by DRM issues and fixes: "all" of them, or
  // one of them as "issue:YYYYMM" / "fix:YYYYMM", or the tracks of one Discovery Mode month as "dm:YYYYMM". Only the chart's revenue changes, never the other sections.
  const [drmFilter, setDrmFilter] = useState<string | null>(null)
  const [filteredRevenue, setFilteredRevenue] = useState<AdvanceDealPlatformRevenue[] | null>(null)
  const [filtering, setFiltering] = useState(false)

  useEffect(() => {
    let cancelled = false
    setDrmFilter(null)
    setLoading(true)
    setFailed(false)
    const royaltyDateParam = reportingPeriod ? "" : "&royalty_date=activity_period"
    users(`/user_advance_portfolio_details/?user_id=${userId}${royaltyDateParam}`, "GET")
      .then((response) => {
        if (cancelled) return
        setDetails(mapToCamelCase<AdvanceDealDetails>(response.data))
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setFailed(true)
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [userId, reportingPeriod])

  useEffect(() => {
    if (!drmFilter) {
      setFilteredRevenue(null)
      setFiltering(false)
      return
    }
    let cancelled = false
    setFiltering(true)
    const [filterKind, filterMonth] = drmFilter.split(":")
    const filterParam =
      drmFilter === "all"
        ? "&drm_filter=all"
        : drmFilter === "dm-all"
        ? "&drm_discovery_mode_filter=all"
        : `&${
            filterKind === "fix"
              ? "drm_fix_month"
              : filterKind === "dm"
              ? "drm_discovery_mode_month"
              : "drm_issue_month"
          }=${filterMonth}`
    const royaltyDateParam = reportingPeriod ? "" : "&royalty_date=activity_period"
    users(`/user_advance_portfolio_details/?user_id=${userId}${filterParam}${royaltyDateParam}`, "GET")
      .then((response) => {
        if (cancelled) return
        const mapped = mapToCamelCase<AdvanceDealDetails>(response.data)
        // Months before the tracks' data starts come without a total; they have no revenue to plot.
        setFilteredRevenue(mapped.platformRevenue.map((row) => ({ ...row, total: row.total ?? 0 })))
        setFiltering(false)
      })
      .catch(() => {
        if (cancelled) return
        setDrmFilter(null)
        setFiltering(false)
      })
    return () => {
      cancelled = true
    }
  }, [drmFilter, userId, reportingPeriod])

  const chartDetails = useMemo(
    () => (details && filteredRevenue ? { ...details, platformRevenue: filteredRevenue } : details),
    [details, filteredRevenue]
  )

  return (
    <>
      <AdvanceRecoupmentSchedule details={details} loading={loading} failed={failed} />
      <PlatformRevenueChart
        details={chartDetails}
        loading={loading}
        failed={failed}
        drmFilter={drmFilter}
        onDrmFilterChange={setDrmFilter}
        filtering={filtering}
        artistName={artistName}
        reportingPeriod={reportingPeriod}
      />
    </>
  )
}

export default AdvanceDealDetailsSections
